import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, User, Mail, Phone, Calendar, UserCircle } from 'lucide-react'
import { formatPhoneDisplay, normaliseRegistrationField } from '@/shared/lib/forms'
import { AppShell } from '@/admin/components/app-shell'
import { CustomerActionControls } from '@/admin/components/shared/customer-action-controls'
import { SharedAddressBadge } from '@/admin/components/shared/address-matches'
import { EditableDetailCard } from '@/admin/components/shared/editable-detail-card'
import type { CardFieldItem } from '@/admin/components/shared/editable-card-types'
import type { ConsumerDetail } from '@/admin/types/products'
import { EntityHistoryCard } from '@/shared/components/workflows/entity-history-card'
import {
  useEntityTypeByKey,
  type EntityAttributeDefinition,
} from '@/admin/hooks/use-entities'
import { useEvaluatedEntityActions } from '@/admin/hooks/use-entity-actions'
import { useConsumerDetail } from '@/admin/hooks/use-products'
import { usePagination } from '@/admin/components/paginated-table'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { useFormatters } from '@/shared/lib/formatting'

/**
 * Read an attribute's current value out of the evaluated resource values.
 *
 * Composite attributes (ADDRESS) live in the payload as their individual
 * component columns, so they are recomposed into the object shape the
 * display formatter expects. The mapping comes from the attribute's
 * ``component_columns`` config, never from a hardcoded column list.
 */
function readAttributeValue(
  attribute: EntityAttributeDefinition,
  values: Record<string, unknown>,
): unknown {
  const componentColumns = attribute.configuration_data?.component_columns as
    | Record<string, string>
    | undefined

  if (componentColumns) {
    const composed: Record<string, unknown> = {}
    let hasAny = false
    for (const [compositeKey, column] of Object.entries(componentColumns)) {
      const value = values[column]
      composed[compositeKey] = value ?? ''
      if (value) hasAny = true
    }
    return hasAny ? composed : null
  }

  return values[attribute.column_name || attribute.code_name] ?? null
}

/**
 * The customer's own details, driven by the consumer entity's registered
 * attributes rather than a hardcoded field list - so a tenant that adds a
 * consumer attribute sees it here without a code change.
 *
 * Editing runs through the same ``EntityActionMenu`` that owns every other
 * customer action: the Edit button externally triggers the configured
 * ``edit_customer`` action, so the dialog, its reason gate, composite
 * handling, and permission gate all stay in one place.
 */
function CustomerDetailsCard({
  consumerUuid,
  consumer,
  onActionComplete,
}: {
  consumerUuid: string
  consumer: ConsumerDetail
  onActionComplete: () => void
}) {
  const { t } = useTranslation()

  const { data: entity } = useEntityTypeByKey('consumer')
  const { data: evaluated } = useEvaluatedEntityActions(entity?.uuid, consumerUuid)

  // `resource_values` is absent when the caller may READ this customer but not
  // ACT on one - a partner opening a customer another partner registered. The
  // row gate that withholds the actions withholds the values with them, so fall
  // back to the record the page already loaded. Tenant-authored EAV attributes
  // are not in that payload and stay blank, which is the right side of the line
  // for a record the caller does not own.
  // `consumer` is React Query's own object, so the reference is stable and this
  // memo holds across renders. Do not hand it a fresh copy (`{...consumer}`) -
  // that busts memoisation here and in the `items` memo below on every render.
  // The cast is a widening to the record shape `readAttributeValue` reads; it
  // allocates nothing.
  const resourceValues = useMemo(
    () =>
      evaluated?.resource_values ??
      (consumer as unknown as Record<string, unknown>),
    [evaluated?.resource_values, consumer],
  )

  const items: CardFieldItem[] = useMemo(() => {
    const attributes = [...(entity?.attributes ?? [])].sort(
      (a, b) => a.order - b.order,
    )

    // A composite attribute (e.g. Mailing Address) already renders every
    // column it is built from. Where a tenant has also registered those
    // columns as attributes in their own right, showing both would list the
    // address twice - once broken apart, once whole. Drop the parts and keep
    // the composite. Read off component_columns, so this holds for any
    // composite on any entity rather than a hardcoded address special case.
    const componentColumns = new Set<string>()
    for (const attribute of attributes) {
      const columns = attribute.configuration_data?.component_columns as
        | Record<string, string>
        | undefined
      if (!columns) continue
      for (const column of Object.values(columns)) {
        if (!column.includes('__')) componentColumns.add(column)
      }
    }

    return attributes
      .filter(
        (attribute) =>
          !componentColumns.has(attribute.column_name || attribute.code_name),
      )
      .map((attribute) => ({
        field: normaliseRegistrationField(attribute),
        value: readAttributeValue(attribute, resourceValues),
      }))
  }, [entity?.attributes, resourceValues])

  if (items.length === 0) return null

  return (
    <EditableDetailCard
      title={t('customers.details', { defaultValue: 'Customer details' })}
      icon={<UserCircle size={16} />}
      items={items}
      collapsible={false}
      trailing={
        <div className="flex items-center gap-2">
          <SharedAddressBadge consumerUuid={consumerUuid} />
          <CustomerActionControls
            consumerUuid={consumerUuid}
            onActionComplete={onActionComplete}
            triggerTestId="customer-actions-menu"
            editButtonTestId="customer-details-edit-btn"
          />
        </div>
      }
    />
  )
}

export function CustomerDetailPage() {
  const { t } = useTranslation()
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { formatDate } = useFormatters()
  const { page, setPage, pageSize, setPageSize } = usePagination(20)

  // A customer edit lands on the ConsumerAccount every registration and claim
  // reads through, so refresh this record and the activity trail that renders
  // the change. The registrations list is FK-resolved, not a denormalised
  // copy, so it picks the new details up on its own next read.
  const handleActionComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['consumer-detail'] })
    queryClient.invalidateQueries({ queryKey: ['workflows', 'entity-log'] })
  }, [queryClient])

  const params = useMemo(
    () => ({ page: String(page), page_size: String(pageSize) }),
    [page, pageSize]
  )

  const { data, isLoading } = useConsumerDetail(uuid ?? null, params)

  if (isLoading) {
    return (
      <AppShell>
        <main className="max-w-4xl mx-auto px-6 py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            {t('common.loading')}
          </p>
        </main>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <main className="max-w-4xl mx-auto px-6 py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            {t('customers.notFound')}
          </p>
        </main>
      </AppShell>
    );
  }

  const { consumer, holdings: holdingsData } = data
  const holdings = holdingsData.results ?? []
  const totalCount = holdingsData.count ?? 0
  const displayName =
    consumer.first_name || consumer.last_name
      ? `${consumer.first_name} ${consumer.last_name}`.trim()
      : consumer.email

  const holdingColumns: DataTableColumn<(typeof holdings)[number]>[] = [
    {
      id: 'product',
      header: t('customers.columnProduct'),
      cell: (row) => row.product?.cached_short_name || row.product?.cached_long_name || '-',
    },
    {
      id: 'registered',
      header: t('customers.columnRegistered'),
      cell: (row) => formatDate(row.created_at),
    },
  ]

  return (
    <AppShell>
      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex cursor-pointer items-center gap-1 text-[11px] text-[var(--ink-3)]"
        >
          <ArrowLeft className="h-3 w-3" />
          {t('customers.backToCustomers', { defaultValue: 'Back to customers' })}
        </button>

        {/* Header */}
        <div className="mb-8 flex items-start gap-4">
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)]"
          >
            <User className="h-6 w-6 text-[var(--green)]" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="clara-title mb-1">{displayName}</h1>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-1.5 text-[12px] text-[var(--ink-3)]">
                <Mail className="h-3 w-3" />
                {consumer.email}
              </span>
              {consumer.phone_number && (
                <span className="flex items-center gap-1.5 text-[12px] text-[var(--ink-3)]">
                  <Phone className="h-3 w-3" />
                  {formatPhoneDisplay(consumer.phone_number)}
                </span>
              )}
              <span className="flex items-center gap-1.5 text-[12px] text-[var(--ink-3)]">
                <Calendar className="h-3 w-3" />
                {t('customers.customerSince')} {formatDate(consumer.created_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Customer details - the editable record itself. Fields, edit, and
            every other customer action come from the consumer entity's
            config, not from a list hardcoded here. */}
        {uuid ? (
          <div className="mb-10">
            <CustomerDetailsCard
              consumerUuid={uuid}
              consumer={consumer}
              onActionComplete={handleActionComplete}
            />
          </div>
        ) : null}

        {/* Registrations */}
        <div>
          <h2
            className="mb-3 border-b-[0.5px] border-b-[var(--rule)] pb-2 text-[10px] font-normal uppercase tracking-[0.12em] text-[var(--ink-3)]"
          >
            {t('customers.registrations')} ({totalCount})
          </h2>

          <DataTable<(typeof holdings)[number]>
            tableId="customer-holdings"
            columns={holdingColumns}
            data={holdings}
            onRowClick={(h) => navigate(`/registrations/${h.uuid}`)}
            emptyMessage={t('customers.noRegistrations')}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>

        {/* Activity - every change to this customer record, with who made
            it, which field moved, and the before/after values. The card
            carries its own heading, so no section title above it. */}
        <div className="mt-10">
          <EntityHistoryCard
            entityKey="consumer"
            entityId={uuid ?? null}
            title={t('customers.activity', { defaultValue: 'Customer activity' })}
          />
        </div>
      </main>
    </AppShell>
  );
}
